import type { ChangeEvent } from 'react';

import { toggleGroupValue } from '@revelio/ui';

import { CardListSelect, type ICardListSelectProps } from './card-list-select';
import CardListSubMenu, {
  type CardListSubMenuProps,
  isColumnCardListSubmMenu,
  type OnCloseSubMenuProp,
} from './card-list-sub-menu';

export type ColumnItem<CardType> =
  | ICardListSelectProps<CardType>
  | CardListSubMenuProps<CardType>;

/** The selection the cards share, in the order they were ticked. */
export interface CardListGroupProps {
  value: string[];
  onChange?: (value: string[]) => void;
}

export interface CardListColumnProps<CardType> {
  variant: 'dataset' | 'columns' | 'delivery' | 'report';
  columnItem: ColumnItem<CardType>;
  hideCtaIfNotSelected?: boolean;
  hideCheckbox?: boolean;
  onCloseSubMenu?: OnCloseSubMenuProp<CardType>;
  checkboxGroupProps: CardListGroupProps;
}

export const CardListColumn = <CardType extends string>({
  columnItem,
  variant,
  hideCtaIfNotSelected,
  hideCheckbox,
  checkboxGroupProps,
  onCloseSubMenu,
}: CardListColumnProps<CardType>) => {
  const selected = checkboxGroupProps.value;
  // Each card is ticked when the group holds its id; a tick appends the id and
  // an untick removes it.
  const getCheckboxProps = ({
    value,
    isDisabled,
  }: {
    value: string;
    isDisabled?: boolean;
  }) => ({
    value,
    isDisabled,
    isChecked: selected.includes(value),
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      checkboxGroupProps.onChange?.(
        toggleGroupValue(selected, value, event.target.checked)
      ),
  });

  if (isColumnCardListSubmMenu(columnItem)) {
    return (
      <CardListSubMenu<CardType>
        variant={variant}
        hideCtaIfNotSelected={hideCtaIfNotSelected}
        hideCheckbox={hideCheckbox}
        onCloseSubMenu={onCloseSubMenu}
        {...columnItem}
        menuItems={columnItem.menuItems.map((col) => ({
          variant,
          hideCtaIfNotSelected,
          hideCheckbox,
          borderColor: 'none',
          ...col,
          ...getCheckboxProps({ value: col.id, isDisabled: col.isDisabled }),
        }))}
      />
    );
  }

  return (
    <CardListSelect<CardType>
      key={columnItem.id}
      hideCtaIfNotSelected={hideCtaIfNotSelected}
      hideCheckbox={hideCheckbox}
      variant={variant}
      {...columnItem}
      {...getCheckboxProps({
        value: columnItem.id,
        isDisabled: columnItem.isDisabled,
      })}
      {...(columnItem.isChecked // this is so you can fix if the card is checked
        ? { isChecked: columnItem.isChecked }
        : {})}
    />
  );
};
